import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { isInternalRoute } from '@stitch/pbx-adapter/catalog';
import { api } from '../../api/client';
import { ImportCard, startImport, type ImportState } from '../../components/ImportCard';
import { LelandPanel } from '../../components/Leland';
import { useTenant } from '../../components/Shell';
import { Button, Card, Notice, PageHeader } from '../../components/ui';
import { plural } from '../../lib/format';
import { Icon, type IconName } from '../../lib/icons';
import { useLeland } from '../../lib/leland';
import { useData } from '../../lib/useData';

interface Step {
  id: string;
  title: string;
  icon: IconName;
  done: boolean | null;
  detail: ReactNode;
  action: ReactNode;
  ask?: string;
  optional?: boolean;
}

/**
 * Get set up: a new company's admin builds out their phone system with Leland, starting with a
 * spreadsheet of their people. The checklist beside it reads the phone system and ticks itself as
 * things are added (it reloads whenever something changes).
 */
export function TenantSetup() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const base = `/t/${encodeURIComponent(d)}`;
  const l = useLeland();
  // One place at a time: the floating panel closes while this page is open.
  useEffect(() => {
    l?.setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const people = useData(() => api.people(d).catch(() => null), [d]);
  const numbers = useData(() => api.numbers(d).catch(() => null), [d]);
  const e911 = useData(() => api.e911(d).catch(() => null), [d]);
  const phones = useData(() => api.accounts(d, 'phone').catch(() => null), [d]);
  const access = useData(() => api.access(d).catch(() => null), [d]);

  const ownNumbers = numbers.data?.numbers.filter((n) => !isInternalRoute(n.number)) ?? [];
  const going = ownNumbers.filter((n) => n.destination).length;
  const without911 = e911.data?.carrier ? e911.data.numbers.filter((n) => !n.tollFree && !n.address).length : null;
  const team = access.data ? access.data.members.length + access.data.invites.length : 0;
  const count = people.data?.people.length ?? 0;

  const steps: Step[] = [
    {
      id: 'people',
      title: 'Your people',
      icon: 'users',
      done: people.data ? count > 0 : null,
      detail: people.data ? (count ? `${plural(count, 'person', 'people')} so far` : 'No one yet') : '…',
      action: <Link to={`${base}/people`}>People</Link>,
      ask: 'Help me add our people'
    },
    {
      id: 'numbers',
      title: 'Phone numbers',
      icon: 'hash',
      done: numbers.data ? ownNumbers.length > 0 : null,
      detail: numbers.data ? (ownNumbers.length ? plural(ownNumbers.length, 'number') : 'New numbers, or move yours in') : '…',
      action: (
        <span className="setup-links">
          <Link to={`${base}/numbers/new`}>Get numbers</Link>
          <Link to={`${base}/numbers/move`}>Move numbers to Stitch</Link>
        </span>
      )
    },
    {
      id: 'e911',
      title: 'Emergency (911) addresses',
      icon: 'pin',
      done: without911 === null ? null : ownNumbers.length > 0 && without911 === 0,
      detail: without911 === null ? 'Set with Stitch support' : !ownNumbers.length ? 'Once you have numbers' : without911 ? `${plural(without911, 'number')} without one` : 'Every number has one',
      action: <Link to={`${base}/emergency`}>Emergency addresses</Link>
    },
    {
      id: 'routing',
      title: 'Where calls go',
      icon: 'flow',
      done: numbers.data ? ownNumbers.length > 0 && going === ownNumbers.length : null,
      detail: !ownNumbers.length ? 'Your main number, hours, groups and queues' : going === ownNumbers.length ? 'Every number goes somewhere' : `${plural(ownNumbers.length - going, 'number')} going nowhere yet`,
      action: <Link to={`${base}/numbers`}>Numbers</Link>,
      ask: 'Help me set up where our calls go: our main number, business hours and who answers'
    },
    {
      id: 'phones',
      title: 'Desk phones',
      icon: 'device',
      optional: true,
      done: phones.data ? phones.data.accounts.length > 0 : null,
      detail: phones.data ? (phones.data.accounts.length ? plural(phones.data.accounts.length, 'phone') : 'If you use them') : '…',
      action: <Link to={`${base}/phones`}>Desk phones</Link>
    },
    {
      id: 'team',
      title: 'Your team in Stitch',
      icon: 'key',
      done: access.data ? team > 1 : null,
      detail: access.data ? (team > 1 ? `${plural(team, 'person', 'people')} with access` : 'Invite other admins, or people to see their own phone') : '…',
      action: <Link to={`${base}/team`}>Portal access</Link>
    }
  ];
  const doneCount = steps.filter((s) => s.done).length;

  return (
    <>
      <PageHeader eyebrow={`${tenant.name} · ${doneCount} of ${steps.length} done`} title="Get set up" />
      <div className="content setup-page">
        <div className="setup-grid">
          <div className="setup-main">
            {l?.enabled === false ? (
              <OnYourOwn domain={d} base={base} />
            ) : (
              <>
                <Notice icon="spark" title="Set up your phone system with Leland">
                  Attach a spreadsheet of your people (Excel or CSV) with the paper clip, or just tell Leland about your team. You see every change before it’s made.
                </Notice>
                <LelandPanel variant="page" base={base} />
              </>
            )}
          </div>
          <aside className="setup-side">
            <Card title="Your setup">
              <ol className="setup-steps">
                {steps.map((s) => (
                  <li key={s.id} className={s.done ? 'done' : s.done === null ? 'loading' : 'todo'}>
                    <span className="setup-step-icon" aria-hidden="true">
                      <Icon name={s.done ? 'check' : s.icon} size={16} strokeWidth={2.2} />
                    </span>
                    <span className="cell-stack grow">
                      <strong>
                        {s.title}
                        {s.optional && <span className="muted small-print"> · optional</span>}
                      </strong>
                      <small className="muted">{s.detail}</small>
                      <span className="setup-step-actions">
                        {s.action}
                        {s.ask && l?.enabled && !s.done && (
                          <button type="button" className="link-btn" onClick={() => void l.send(s.ask!)} disabled={l.busy}>
                            Ask Leland
                          </button>
                        )}
                      </span>
                    </span>
                    <span className="sr-only">{s.done ? 'Done' : 'To do'}</span>
                  </li>
                ))}
              </ol>
            </Card>
            <Card title="Spreadsheet templates">
              <div className="card-body section-stack">
                <p className="muted small">
                  Any list of your people works, with whatever columns it has. Starting from scratch? These have the columns Stitch looks for: first and last name, extension, email,
                  mobile, and kind of user.
                </p>
                <div className="setup-links">
                  <a className="btn secondary small" href="/templates/stitch-people-template.xlsx" download>
                    <Icon name="download" size={15} strokeWidth={2.2} /> Excel template
                  </a>
                  <a className="btn ghost small" href="/templates/stitch-people-template.csv" download>
                    <Icon name="download" size={15} strokeWidth={2.2} /> CSV
                  </a>
                </div>
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </>
  );
}

/** Without Leland (not switched on): upload the file and review it here. */
function OnYourOwn({ domain, base }: { domain: string; base: string }) {
  const [state, setState] = useState<ImportState | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const pick = async (file: File) => {
    setBusy(true);
    setError('');
    try {
      const { id } = await api.uploadSheet(domain, file);
      setState(startImport((await api.planImport(domain, id, {})).import));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Add your people from a spreadsheet">
      <div className="card-body section-stack">
        {!state || state.status.phase !== 'review' ? (
          <div className="setup-drop">
            <p>Upload an Excel (.xlsx) or CSV file with one person per row. Stitch works out which column is which, and you check every row before anyone is added.</p>
            <input
              ref={picker}
              type="file"
              hidden
              accept=".xlsx,.csv,.tsv,.txt,.xls"
              aria-label="Spreadsheet of people"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void pick(f);
              }}
            />
            <Button variant="primary" icon="upload" disabled={busy} onClick={() => picker.current?.click()}>
              {busy ? 'Reading…' : state ? 'Upload another file' : 'Upload a spreadsheet'}
            </Button>
          </div>
        ) : null}
        {error && <Notice tone="crit">{error}</Notice>}
        {state && <ImportCard domain={domain} state={state} update={(fn) => setState((s) => (s ? fn(s) : s))} viaLeland={false} base={base} />}
      </div>
    </Card>
  );
}
